import { FC, useState } from "react";

import { Stack } from "@mui/material";
import { TicketingFormCategory, OrganizationRoutes } from "@simplyk/common";

import { AmplitudeEvents } from "../../../../../common/constants/amplitude";
import { useTranslate } from "../../../../../common/hooks/useTranslate";
import { ExternalLink as LaunchIcon, Share as ShareIcon, Bell as BellIcon } from "../../../../../icons/outlined";

import { CardWithIcon } from "./CardWithIcon";

import { FormStatus } from "@/common/types/trpc";
import { LiveEditorStepLayout } from "@/components/LiveEditorElements/LiveEditorStepLayout/LiveEditorStepLayout";

type StepReviewStatelessProps = {
  title: string;
  description?: string;
  isOnboarding: boolean;
  formLink: string;
  showCommunicationCard: boolean;
  formId: string;
  onShareForm: () => void;
  onLogUserAction: (event: AmplitudeEvents) => void;
  onNavigate: (path: string) => Promise<unknown>;
  communicationTexts: {
    title: string;
    description: string;
  };
  formStatus: FormStatus;
  formCategory?: TicketingFormCategory;
};

export const StepReviewStateless: FC<StepReviewStatelessProps> = ({
  title,
  description,
  isOnboarding,
  formLink,
  showCommunicationCard,
  formId,
  onShareForm,
  onLogUserAction,
  onNavigate,
  communicationTexts,
  formStatus,
}) => {
  const { t } = useTranslate();
  const [loadingCardIndex, setLoadingCardIndex] = useState<number | null>(null);

  const handleCardClick = async ({
    action,
    index,
    amplitudeEvent,
  }: {
    action: () => Promise<void>;
    index: number;
    amplitudeEvent: AmplitudeEvents;
  }) => {
    setLoadingCardIndex(index);
    onLogUserAction(amplitudeEvent);
    try {
      await action();
    } finally {
      setLoadingCardIndex(null);
    }
  };

  const seeMyFormCard = () => {
    return (
      <CardWithIcon
        icon={<LaunchIcon />}
        intensity="positive"
        title={t("common", "seeMyForm")}
        data-test="see-my-form-card"
        onClick={() =>
          handleCardClick({
            action: async () => {
              await onNavigate(formLink);
            },
            index: 0,
            amplitudeEvent: AmplitudeEvents.FormViewButtonClicked,
          })
        }
        loading={loadingCardIndex === 0}
      />
    );
  };

  const defaultActions = () => {
    return (
      <Stack direction="column" spacing={2}>
        {seeMyFormCard()}

        <CardWithIcon
          icon={<ShareIcon />}
          intensity="brand"
          title={t("dashboard", "common.shareMyForm")}
          data-test="share-my-form-card"
          onClick={onShareForm}
          loading={loadingCardIndex === 1 || formStatus === "draft"}
        />

        {showCommunicationCard && (
          <CardWithIcon
            icon={<BellIcon />}
            intensity="warning"
            title={communicationTexts.title}
            description={communicationTexts.description}
            data-test="communication-card"
            onClick={() =>
              handleCardClick({
                action: async () => {
                  await onNavigate(`${OrganizationRoutes.CampaignHub}?formId=${formId}&tab=communication`);
                },
                index: 2,
                amplitudeEvent: AmplitudeEvents.FormCommunicationClicked,
              })
            }
            loading={loadingCardIndex === 2}
          />
        )}
      </Stack>
    );
  };

  return (
    <LiveEditorStepLayout
      slots={{
        content: (
          <Stack gap={2}>
            {/* Step Actions */}
            {isOnboarding ? seeMyFormCard() : defaultActions()}
          </Stack>
        ),
      }}
      title={title}
      description={isOnboarding && description ? description : undefined}
      data-test="step-review"
    />
  );
};
